import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/contexts/AuthContext';
import type { SubscriptionPlan } from '@/types/saas';

export default function TenantSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { createTenant } = useTenant();
  
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  
  const planId = location.state?.planId;

  useEffect(() => {
    if (!user) {
      navigate('/signup', { state: { planId } });
      return;
    }
    
    if (planId) {
      loadPlan(planId);
    }
  }, [user, planId]);

  const loadPlan = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setSelectedPlan(data as SubscriptionPlan);
    } catch (error) {
      console.error('Error loading plan:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل تفاصيل الخطة',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!companyName.trim()) {
      toast({
        title: 'خطأ',
        description: 'يرجى إدخال اسم الشركة',
        variant: 'destructive'
      });
      return;
    }

    if (!selectedPlan) {
      toast({
        title: 'خطأ',
        description: 'يرجى اختيار خطة الاشتراك',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const result = await createTenant(companyName, selectedPlan.id);
      
      if (result.success) {
        toast({
          title: 'نجح التسجيل',
          description: 'تم إنشاء حساب شركتك بنجاح. ستبدأ فترتك التجريبية المجانية الآن.',
        });
        
        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        throw result.error;
      }
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast({
        title: 'خطأ في التسجيل',
        description: 'حدث خطأ أثناء إنشاء حساب الشركة. يرجى المحاولة مرة أخرى.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">إنشاء حساب الشركة</CardTitle>
          <CardDescription>
            أدخل معلومات شركتك للبدء في فترة التجربة المجانية
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {selectedPlan && (
            <div className="mb-6 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">الخطة المختارة</p>
              <p className="font-semibold">{selectedPlan.name_ar}</p>
              <p className="text-sm text-muted-foreground">
                {selectedPlan.price} ريال/{selectedPlan.type === 'monthly' ? 'شهر' : 'سنة'}
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company">اسم الشركة</Label>
              <Input
                id="company"
                type="text"
                placeholder="أدخل اسم شركتك"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={loading}
                required
              />
              <p className="text-xs text-muted-foreground">
                سيتم استخدام هذا الاسم في جميع أنحاء النظام
              </p>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    جاري الإنشاء...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    ابدأ الفترة التجريبية المجانية
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              <p>فترة تجريبية مجانية لمدة 14 يوم</p>
              <p>لا حاجة لبطاقة ائتمان • إلغاء في أي وقت</p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}